import { Chain } from '@starsoft/common/models';
import { FCOptionProps } from '../../props';
import styles from './styles.module.scss';
import { useTranslation } from '../../../../hooks';
import { getContrastColor } from '@starsoft/common/utils';

/**
 * Component that renders a single blockchain chain option in the autocomplete selector
 * Displays chain details including name, symbol with colored chip, and confirmation count
 *
 * @param {Object} props - Component props
 * @param {Function} props.handleSelect - Callback function when chain is selected
 * @param {Chain} props.option - The chain data to display
 * @param {boolean} props.selected - Whether this chain option is currently selected
 * @returns {JSX.Element} Chain option component with name, colored chip and confirmation count
 */
export function ChainOption({
  handleSelect,
  option,
  selected,
}: Omit<FCOptionProps<Chain>, 'key'>) {
  const { t } = useTranslation('common');

  /**
   * Handles clicking the chain option
   * Calls the handleSelect callback with the chain data
   */
  function handleClick() {
    handleSelect(option);
  }

  return (
    <div
      className={`${styles.container} ${selected ? styles['container--selected'] : ''}`}
      onClick={handleClick}
    >
      <div className={styles.container__title}>
        {option.name}{' '}
        <div
          className={styles.container__chip}
          style={{
            backgroundColor: option?.color,
            color: getContrastColor(option?.color),
          }}
        >
          {option?.symbol}
        </div>
      </div>
      <p className={styles.container__subtitle}>
        {t('chain_confirmation_count', {
          confirmations: option?.confirmations,
        })}
      </p>
    </div>
  );
}
